import type { LearnerLevel } from '@vuganawe/types';

/**
 * Placement test bank: 20 questions ordered easy → hard — 16 "Use of English"
 * (grammar & vocabulary) items plus 4 listening items (marked with `audio`,
 * spoken aloud via TTS). The score maps to a recommended level in
 * {@link recommendLevel}.
 *
 * Method follows the standard, CEFR-aligned placement format used by the Oxford
 * Placement Test, Cambridge, and EF SET: auto-scored multiple-choice
 * Use-of-English + Listening items graded from A1 to C1, scored to a
 * recommended CEFR band (see {@link LEVEL_CEFR}). (Speaking and writing are
 * deliberately left out — they can't be auto-scored in a quick screener.)
 */

export interface PlacementQuestion {
  question: string;
  options: string[];
  answer: string;
  /**
   * Listening items only: the sentence spoken aloud (Web Speech API / TTS).
   * The visible `question` asks about it; the answer is in the audio.
   */
  audio?: string;
  /** Optional per-question time override (listening items need longer). */
  seconds?: number;
}

export const PLACEMENT_QUESTIONS: PlacementQuestion[] = [
  {
    question: '___ name is Aline.',
    options: ['My', 'Me', 'I', 'Mine'],
    answer: 'My',
  },
  {
    question: 'I ___ a student.',
    options: ['am', 'is', 'are', 'be'],
    answer: 'am',
  },
  {
    question: 'She ___ from Rwanda.',
    options: ['is', 'are', 'am', 'be'],
    answer: 'is',
  },
  {
    question: 'He ___ to work by bus every day.',
    options: ['goes', 'go', 'going', 'gone'],
    answer: 'goes',
  },
  {
    // Listening · A2
    audio: 'The bank opens at nine o’clock.',
    question: 'Listen: What time does the bank open?',
    options: ['Nine o’clock', 'Ten o’clock', 'Eight o’clock', 'Twelve o’clock'],
    answer: 'Nine o’clock',
    seconds: 25,
  },
  {
    question: 'There aren’t ___ mangoes left in the basket.',
    options: ['any', 'some', 'a', 'much'],
    answer: 'any',
  },
  {
    question: 'Yesterday I ___ my cousin at the market.',
    options: ['saw', 'see', 'seen', 'sees'],
    answer: 'saw',
  },
  {
    question: 'How ___ sugar do you take in your tea?',
    options: ['much', 'many', 'lots', 'few'],
    answer: 'much',
  },
  {
    // Listening · A2/B1
    audio: 'I’d like a cup of coffee, please.',
    question: 'Listen: What does the speaker want?',
    options: ['Coffee', 'Tea', 'Water', 'Juice'],
    answer: 'Coffee',
    seconds: 25,
  },
  {
    question: 'I have lived in Kigali ___ 2019.',
    options: ['since', 'for', 'from', 'at'],
    answer: 'since',
  },
  {
    question: 'If it rains tomorrow, we ___ the meeting online.',
    options: ['will hold', 'held', 'hold', 'would held'],
    answer: 'will hold',
  },
  {
    // Listening · B1
    audio: 'The train to Kigali has been delayed by twenty minutes.',
    question: 'Listen: What happened to the train?',
    options: ['It is delayed', 'It is cancelled', 'It left early', 'It is full'],
    answer: 'It is delayed',
    seconds: 25,
  },
  {
    question: 'You ___ park here — it is reserved for ambulances.',
    options: ['mustn’t', 'don’t have to', 'couldn’t', 'wouldn’t'],
    answer: 'mustn’t',
  },
  {
    question: 'That is the woman ___ shop won the small-business award.',
    options: ['whose', 'who', 'which', 'that'],
    answer: 'whose',
  },
  {
    question: 'By the time we arrived, the meeting ___.',
    options: ['had already started', 'has already started', 'already starts', 'was already start'],
    answer: 'had already started',
  },
  {
    // Listening · B2
    audio: 'If I were you, I’d apply for the manager position.',
    question: 'Listen: What is the speaker doing?',
    options: ['Giving advice', 'Making a complaint', 'Asking a favour', 'Refusing an offer'],
    answer: 'Giving advice',
    seconds: 25,
  },
  {
    question: 'The report ___ by the finance team before Friday.',
    options: ['will have been reviewed', 'will review', 'is reviewing', 'has reviewed'],
    answer: 'will have been reviewed',
  },
  {
    question: 'I’d rather you ___ the client before sending the invoice.',
    options: ['called', 'call', 'would call', 'to call'],
    answer: 'called',
  },
  {
    question: '___ had she finished the presentation than the clients raised objections.',
    options: ['No sooner', 'Hardly', 'Not only', 'Scarcely'],
    answer: 'No sooner',
  },
  {
    question: 'The project would have failed ___ the community not supported it.',
    options: ['had', 'if', 'should', 'were'],
    answer: 'had',
  },
];

/**
 * Map a raw score to the level a learner should start from. Thresholds are
 * proportional (share of questions correct) so the mapping stays correct as the
 * bank grows — these bands reproduce the original 16-question cut-offs.
 */
export function recommendLevel(correct: number, total = PLACEMENT_QUESTIONS.length): LearnerLevel {
  const share = total > 0 ? correct / total : 0;
  if (share <= 0.3) return 'BEGINNER';
  if (share <= 0.55) return 'ELEMENTARY';
  if (share <= 0.72) return 'INTERMEDIATE';
  if (share <= 0.9) return 'UPPER_INTERMEDIATE';
  return 'ADVANCED';
}

/**
 * Time allowance per question, in seconds. A fixed, quick pace keeps the
 * screener snappy. (Full CEFR placement tests like EF SET run closer to
 * 30–60s/question; 15s here is a deliberate fast-screener choice — change this
 * single constant to give more or less thinking time.)
 */
export const SECONDS_PER_QUESTION = 15;

/** Our internal levels expressed as the internationally recognised CEFR bands. */
export const LEVEL_CEFR: Record<LearnerLevel, string> = {
  BEGINNER: 'A1',
  ELEMENTARY: 'A2',
  INTERMEDIATE: 'B1',
  UPPER_INTERMEDIATE: 'B2',
  ADVANCED: 'C1',
};

export const LEVEL_ADVICE: Record<LearnerLevel, string> = {
  BEGINNER:
    'Start from the very beginning — greetings, introductions, and everyday phrases. You will make fast progress with short daily practice.',
  ELEMENTARY:
    'You know the basics. Focus on everyday conversations, present and past tenses, and practical vocabulary for work and services.',
  INTERMEDIATE:
    'You can handle daily communication. Work on fluency, richer vocabulary, and more natural grammar in real situations.',
  UPPER_INTERMEDIATE:
    'You communicate well. Push into nuance — advanced structures, precise vocabulary, and confident speaking in professional settings.',
  ADVANCED:
    'Your English is strong. Polish the fine details and keep speaking practice regular to stay sharp.',
};
